import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { LOGO } from '../../assets/media';

const Bar = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: ${({ theme }) => theme.zIndex.header};
  background: ${({ $transparent }) =>
    $transparent ? 'transparent' : 'rgba(24, 22, 18, 0.94)'};
  backdrop-filter: ${({ $transparent }) => ($transparent ? 'none' : 'blur(8px)')};
  border-bottom: 1px solid
    ${({ $transparent }) => ($transparent ? 'transparent' : 'rgba(255, 255, 255, 0.08)')};
  transition: background 300ms ease, border-color 300ms ease;
`;

const Inner = styled.div`
  max-width: 1160px;
  margin: 0 auto;
  padding: 0.7rem 1.25rem;
  display: flex;
  align-items: center;
  justify-content: space-between;

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: 0.85rem 2rem;
  }
`;

/* Logo real de la marca, en marco redondo */
const Logo = styled(Link)`
  display: flex;
  align-items: center;
  gap: 0.6rem;
`;

const LogoImg = styled.img`
  width: 42px;
  height: 42px;
  border-radius: 50%;
  border: 2px solid #f4f3ee;
  object-fit: cover;
  transition: border-color 200ms ease;

  ${Logo}:hover & {
    border-color: ${({ theme }) => theme.colors.gold};
  }
`;

const LogoText = styled.span`
  font-family: ${({ theme }) => theme.fonts.heading};
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.2em;
  line-height: 1.4;
  color: #f4f3ee;
  transition: color 200ms ease;

  span {
    display: block;
  }

  ${Logo}:hover & {
    color: ${({ theme }) => theme.colors.gold};
  }
`;

const Toggle = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  cursor: pointer;
  color: #f4f3ee;

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    display: none;
  }
`;

const Nav = styled.nav`
  display: ${({ $open }) => ($open ? 'flex' : 'none')};
  flex-direction: column;
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: #181612;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  padding: 1rem 1.25rem 1.5rem;
  gap: 0.25rem;

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    display: flex;
    flex-direction: row;
    position: static;
    background: none;
    border: none;
    padding: 0;
    gap: 1.6rem;
  }
`;

const NavItem = styled(Link)`
  display: flex;
  align-items: center;
  min-height: 44px;
  padding: 0.5rem 0.25rem;
  font-family: ${({ theme }) => theme.fonts.heading};
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.2em;
  color: ${({ $active, theme }) => ($active ? theme.colors.gold : '#cfccc3')};
  border-bottom: 2px solid ${({ $active, theme }) => ($active ? theme.colors.gold : 'transparent')};
  transition: color 200ms ease, border-color 200ms ease;

  &:hover {
    color: ${({ $active, theme }) => ($active ? theme.colors.gold : '#ffffff')};
  }
`;

// Enlaces por ancla dentro del home (se resaltan según la sección visible)
// y enlaces a otras rutas (Menú y Reservación van al final).
const HASH_LINKS = [
  { hash: '', label: 'Inicio' },
  { hash: 'nosotros', label: 'Nosotros' },
  { hash: 'eventos', label: 'Eventos' },
  { hash: 'galeria', label: 'Galería' },
  { hash: 'contacto-info', label: 'Contacto' }
];

const ROUTE_LINKS = [
  { to: '/menu', label: 'Menú' },
  { to: '/reservaciones', label: 'Reservación' }
];

function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeHash, setActiveHash] = useState('');
  const { pathname } = useLocation();
  const isHome = pathname === '/';

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Scroll-spy: detecta qué sección del home está cruzando el centro de la
  // pantalla para resaltar solo ese enlace en el nav.
  useEffect(() => {
    if (!isHome) return undefined;

    const ids = HASH_LINKS.filter((l) => l.hash).map((l) => l.hash);
    const elements = ids.map((id) => document.getElementById(id)).filter(Boolean);
    if (!elements.length) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting);
        if (visible.length > 0) {
          const topMost = visible.reduce((a, b) =>
            a.boundingClientRect.top < b.boundingClientRect.top ? a : b
          );
          setActiveHash(topMost.target.id);
        }
      },
      { rootMargin: '-45% 0px -50% 0px', threshold: 0 }
    );

    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [isHome]);

  // Sobre el hero del home el nav flota transparente; al hacer scroll
  // (o en cualquier otra página, o con el menú móvil abierto) se vuelve sólido.
  const transparent = isHome && !scrolled && !open;

  return (
    <Bar $transparent={transparent}>
      <Inner>
        <Logo to="/" onClick={() => setOpen(false)} aria-label="Vete a la Chingada, inicio">
          <LogoImg src={LOGO} alt="" />
          <LogoText>
            <span>Vete a la</span>
            <span>Chingada</span>
          </LogoText>
        </Logo>
        <Toggle
          type="button"
          aria-label={open ? 'Cerrar menú' : 'Abrir menú'}
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            {open ? (
              <path d="M6 6L18 18M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            ) : (
              <path d="M3 6H21M3 12H21M3 18H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            )}
          </svg>
        </Toggle>
        <Nav $open={open}>
          {HASH_LINKS.map((link) => (
            <NavItem
              key={link.label}
              to={link.hash ? `/#${link.hash}` : '/'}
              $active={isHome && activeHash === link.hash}
              onClick={() => setOpen(false)}
            >
              {link.label}
            </NavItem>
          ))}
          {ROUTE_LINKS.map((link) => (
            <NavItem
              key={link.label}
              to={link.to}
              $active={pathname === link.to}
              onClick={() => setOpen(false)}
            >
              {link.label}
            </NavItem>
          ))}
        </Nav>
      </Inner>
    </Bar>
  );
}

export default Header;
